import {
  CertificateElement,
  CertificatePageSettings,
  CertificateTemplate,
} from "../certificatePrint/certificateTypes";
import { DEFAULT_CERTIFICATE_LOGO } from "../certificatePrint/certificateTemplates";

const INK = "#1e293b";
const MUTED = "#64748b";
const NAVY = "#0f172a";

export const DEFAULT_NEW_BILL_TEMPLATE_ID = "standard-bill";

const PAGE: CertificatePageSettings = {
  size: "A4",
  orientation: "portrait",
  marginMm: 12,
  fontFamily: "Helvetica, Arial, sans-serif",
  fontSize: 10,
  fontWeight: 400,
  lineHeight: 1.35,
  textAlign: "left",
  logoSrc: DEFAULT_CERTIFICATE_LOGO,
  letterheadSrc: "",
  logoSizeMm: 22,
  fieldSpacing: 1,
  paragraphSpacing: 2,
  labelValueStyle: "inline",
  showSafeArea: false,
  headerAlign: "left",
  footerText: "Thank you for visiting",
};

const text = (
  partial: Omit<
    CertificateElement,
    "type" | "visible" | "lineHeight" | "align" | "fontWeight" | "fontSize"
  > & {
    visible?: boolean;
    lineHeight?: number;
    align?: CertificateElement["align"];
    fontWeight?: number;
    fontSize?: number;
  }
): CertificateElement => ({
  type: "text",
  visible: true,
  lineHeight: 1.35,
  align: "left",
  fontWeight: 400,
  fontSize: 9,
  color: INK,
  ...partial,
});

const table = (partial: Parameters<typeof text>[0]): CertificateElement => ({
  ...text(partial),
  type: "table",
});

function standardElements(): CertificateElement[] {
  return [
    {
      id: "letterhead",
      type: "logo",
      label: "Letterhead",
      section: "header",
      x: 12,
      y: 6,
      width: 186,
      height: 28,
      content: "",
      visible: false,
      fontSize: 10,
      fontWeight: 400,
      align: "center",
      lineHeight: 1,
      fieldKey: "letterhead",
    },
    {
      id: "logo",
      type: "logo",
      label: "Clinic logo",
      section: "header",
      x: 12,
      y: 10,
      width: 28,
      height: 18,
      content: "",
      visible: true,
      fontSize: 10,
      fontWeight: 400,
      align: "left",
      lineHeight: 1,
    },
    text({
      id: "clinicName",
      label: "Clinic name",
      section: "header",
      x: 44,
      y: 10,
      width: 100,
      height: 8,
      content: "{{clinicName}}",
      fontSize: 14,
      fontWeight: 700,
      color: NAVY,
      fieldKey: "clinicName",
    }),
    text({
      id: "clinicAddress",
      label: "Clinic address",
      section: "header",
      x: 44,
      y: 18,
      width: 100,
      height: 5,
      content: "{{clinicAddress}}",
      fontSize: 8,
      color: MUTED,
      fieldKey: "clinicAddress",
    }),
    text({
      id: "clinicPhone",
      label: "Clinic phone",
      section: "header",
      x: 44,
      y: 23,
      width: 100,
      height: 5,
      content: "Tel: {{clinicPhone}}",
      fontSize: 8,
      color: MUTED,
      fieldKey: "clinicPhone",
    }),
    text({
      id: "invoiceNotext",
      label: "Invoice No label",
      section: "header",
      x: 148,
      y: 10,
      width: 24,
      height: 5,
      content: "Invoice No",
      fontSize: 8,
      fontWeight: 600,
      color: MUTED,
    }),
    text({
      id: "invoiceNo",
      label: "Invoice number",
      section: "header",
      x: 172,
      y: 10,
      width: 26,
      height: 5,
      content: "{{invoiceNo}}",
      fontSize: 8,
      fontWeight: 600,
      fieldKey: "invoiceNo",
    }),
    text({
      id: "datetimetext",
      label: "Date/Time label",
      section: "header",
      x: 148,
      y: 16,
      width: 24,
      height: 5,
      content: "Date/Time",
      fontSize: 8,
      fontWeight: 600,
      color: MUTED,
    }),
    text({
      id: "datetime",
      label: "Date/Time",
      section: "header",
      x: 172,
      y: 16,
      width: 26,
      height: 5,
      content: "{{invoiceDate}}",
      fontSize: 8,
      fieldKey: "invoiceDate",
    }),
    text({
      id: "regNotext",
      label: "Reg No label",
      section: "header",
      x: 148,
      y: 22,
      width: 24,
      height: 5,
      content: "Reg No",
      fontSize: 8,
      fontWeight: 600,
      color: MUTED,
    }),
    text({
      id: "regNo",
      label: "Registration number",
      section: "header",
      x: 172,
      y: 22,
      width: 26,
      height: 5,
      content: "{{regNo}}",
      fontSize: 8,
      fieldKey: "regNo",
    }),
    text({
      id: "patientNametext",
      label: "Patient name label",
      section: "body",
      x: 12,
      y: 38,
      width: 28,
      height: 5,
      content: "Patient Name",
      fontSize: 8,
      fontWeight: 600,
      color: MUTED,
    }),
    text({
      id: "patientName",
      label: "Patient name",
      section: "body",
      x: 42,
      y: 38,
      width: 50,
      height: 5,
      content: "{{patientName}}",
      fontSize: 9,
      fontWeight: 600,
      fieldKey: "patientName",
    }),
    text({
      id: "agetext",
      label: "Age label",
      section: "body",
      x: 12,
      y: 44,
      width: 28,
      height: 5,
      content: "Age",
      fontSize: 8,
      fontWeight: 600,
      color: MUTED,
    }),
    text({
      id: "age",
      label: "Age",
      section: "body",
      x: 42,
      y: 44,
      width: 20,
      height: 5,
      content: "{{patientAge}}",
      fontSize: 9,
      fieldKey: "patientAge",
    }),
    text({
      id: "contactNotext",
      label: "Contact label",
      section: "body",
      x: 96,
      y: 38,
      width: 24,
      height: 5,
      content: "Cont. No.",
      fontSize: 8,
      fontWeight: 600,
      color: MUTED,
    }),
    text({
      id: "contactNo",
      label: "Contact number",
      section: "body",
      x: 122,
      y: 38,
      width: 36,
      height: 5,
      content: "{{patientMobile}}",
      fontSize: 9,
      fieldKey: "patientMobile",
    }),
    text({
      id: "gendertext",
      label: "Gender label",
      section: "body",
      x: 160,
      y: 38,
      width: 16,
      height: 5,
      content: "Gender",
      fontSize: 8,
      fontWeight: 600,
      color: MUTED,
    }),
    text({
      id: "gender",
      label: "Gender",
      section: "body",
      x: 178,
      y: 38,
      width: 20,
      height: 5,
      content: "{{patientGender}}",
      fontSize: 9,
      fieldKey: "patientGender",
    }),
    table({
      id: "treatmentDetail",
      label: "Treatment details",
      section: "body",
      x: 12,
      y: 52,
      width: 186,
      height: 70,
      content: "Treatments",
      fontSize: 8,
      fieldKey: "treatmentTable",
    }),
    table({
      id: "billDetails",
      label: "Amounts",
      section: "body",
      x: 110,
      y: 128,
      width: 88,
      height: 80,
      content: "Amounts",
      fontSize: 8,
      fieldKey: "amountTable",
      paddingMm: 1.2,
    }),
    text({
      id: "addresstext",
      label: "Address label",
      section: "footer",
      x: 12,
      y: 270,
      width: 18,
      height: 5,
      content: "Address",
      fontSize: 8,
      fontWeight: 600,
      color: MUTED,
    }),
    text({
      id: "address",
      label: "Address",
      section: "footer",
      x: 32,
      y: 270,
      width: 60,
      height: 5,
      content: "{{clinicAddress}}",
      fontSize: 8,
      fieldKey: "clinicAddress",
    }),
    text({
      id: "dentisttext",
      label: "Dentist label",
      section: "footer",
      x: 96,
      y: 270,
      width: 18,
      height: 5,
      content: "Dentist",
      fontSize: 8,
      fontWeight: 600,
      color: MUTED,
    }),
    text({
      id: "dentist",
      label: "Dentist name",
      section: "footer",
      x: 116,
      y: 270,
      width: 42,
      height: 5,
      content: "{{doctorName}}",
      fontSize: 8,
      fieldKey: "doctorName",
    }),
    text({
      id: "designationtext",
      label: "Designation label",
      section: "footer",
      x: 96,
      y: 276,
      width: 18,
      height: 5,
      content: "Designation",
      fontSize: 8,
      fontWeight: 600,
      color: MUTED,
    }),
    text({
      id: "designation",
      label: "Staff designation",
      section: "footer",
      x: 116,
      y: 276,
      width: 42,
      height: 5,
      content: "{{doctorDesignation}}",
      fontSize: 8,
      fieldKey: "doctorDesignation",
    }),
    text({
      id: "teltext",
      label: "Tel label",
      section: "footer",
      x: 162,
      y: 270,
      width: 10,
      height: 5,
      content: "Tel",
      fontSize: 8,
      fontWeight: 600,
      color: MUTED,
    }),
    text({
      id: "tel",
      label: "Telephone",
      section: "footer",
      x: 174,
      y: 270,
      width: 24,
      height: 5,
      content: "{{clinicPhone}}",
      fontSize: 8,
      fieldKey: "clinicPhone",
    }),
  ];
}

export const DEFAULT_NEW_BILL_TEMPLATES: CertificateTemplate[] = [
  {
    id: DEFAULT_NEW_BILL_TEMPLATE_ID,
    name: "Standard bill",
    page: { ...PAGE },
    elements: standardElements(),
  },
];

export function getDefaultNewBillTemplateById(id: string) {
  return DEFAULT_NEW_BILL_TEMPLATES.find((item) => item.id === id);
}

export function cloneNewBillTemplate(
  template: CertificateTemplate,
  overrides?: Partial<CertificateTemplate>
): CertificateTemplate {
  return {
    ...template,
    ...overrides,
    page: { ...template.page, ...(overrides?.page || {}) },
    elements: (overrides?.elements || template.elements).map((el) => ({ ...el })),
  };
}
